// The practice's own sign-in.
//
// Most of her team will never see SyncSuite — a receptionist opens the
// practice software and types an email and a password, the way they do with
// every other tool they use. SyncSuite's single sign-on still works for
// Matthew and for her, but it is no longer the only door.

import { useEffect, useState, type FormEvent } from "react";
import { Loader2, LogIn } from "lucide-react";
import { authClient } from "@/lib/auth-client";
import { setBearerToken } from "@/lib/auth-store";
import { cn } from "@/lib/utils";

interface Branding { name: string; logo_url: string | null }

/** The token arrives as ?reset=… on the sign-in page itself. */
function resetTokenFromUrl(): string | null {
  try { return new URLSearchParams(window.location.search).get("reset"); }
  catch { return null; }
}

export function LoginScreen({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brand, setBrand] = useState<Branding | null>(null);
  /* "did we set up the changing password so we can create our own" — there
     was no way back in but somebody else resetting you, and the owner has
     nobody above her. */
  const [mode, setMode] = useState<"in" | "forgot">(
    resetTokenFromUrl() ? "in" : "in",
  );
  const [resetToken] = useState<string | null>(resetTokenFromUrl);
  const [newPassword, setNewPassword] = useState("");
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState(false);

  /* Ask for the email that gets the link. Always says the same thing back,
     so this page never confirms whether an address has a login. */
  const askForLink = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      // Called straight, not through the client helper: in better-auth
      // 1.7.7 the route is /request-password-reset and the React client of
      // this version does not expose a matching method.
      await fetch("/api/auth/request-password-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), redirectTo: `${window.location.origin}/` }),
      });
      setSent(true);
    } catch {
      setSent(true);
    } finally { setBusy(false); }
  };

  /* Back from the email, with the token in the address bar. */
  const setNewOne = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await authClient.resetPassword({ newPassword, token: resetToken ?? undefined });
      if ((res as { error?: { message?: string } }).error) {
        setError((res as { error?: { message?: string } }).error?.message
          || "That link has expired. Ask for a new one.");
        return;
      }
      setDone(true);
      window.history.replaceState({}, "", window.location.pathname);
    } catch {
      setError("Couldn't reach the server. Try again in a moment.");
    } finally { setBusy(false); }
  };

  // The practice's own name on its own login page — not "CRM".
  useEffect(() => {
    fetch("/api/public/branding")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setBrand(d.branding ?? null))
      .catch(() => {});
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      // The bearer plugin returns the signed token in the `set-auth-token`
      // RESPONSE HEADER. The `token` in the body is only the session id — using
      // that as a bearer authenticates nothing, which looks exactly like a
      // wrong password.
      let token: string | null = null;
      const res = await authClient.signIn.email(
        { email: email.trim(), password },
        { onSuccess: (ctx) => { token = ctx.response.headers.get("set-auth-token"); } },
      );
      if ((res as { error?: unknown }).error || !token) {
        const msg = ((res as { error?: { message?: string } }).error?.message) ?? "";
        setError(
          /credential|password|email/i.test(msg)
            ? "That email and password don't match."
            : msg || "That email and password don't match.",
        );
        return;
      }
      setBearerToken(token);
      onSignedIn();
    } catch {
      setError("Couldn't reach the server. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          {brand?.logo_url ? (
            <img src={brand.logo_url} alt="" className="mx-auto mb-3 h-12 object-contain" />
          ) : (
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-xl bg-indigo-500/15">
              <LogIn className="h-5 w-5 text-indigo-300" />
            </div>
          )}
          <h1 className="text-lg font-semibold text-zinc-100">
            {brand?.name || "Sign in"}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">Sign in to the practice software.</p>
        </div>

        {/* Back from the email: choose a new one and you are in. */}
        {resetToken && !done && (
          <form onSubmit={setNewOne} className="space-y-3 rounded-2xl border border-zinc-800/70 bg-zinc-900/40 p-5">
            <p className="text-sm text-zinc-300">Choose a new password.</p>
            <div>
              <label htmlFor="newpw" className="mb-1 block text-xs text-zinc-400">New password</label>
              <input
                id="newpw" type="password" autoComplete="new-password" required autoFocus minLength={8}
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-indigo-500"
              />
              <p className="mt-1 text-[11px] text-zinc-500">At least 8 characters.</p>
            </div>
            {error && <p role="alert" className="rounded-lg bg-rose-500/10 px-3 py-2 text-xs text-rose-300">{error}</p>}
            <button
              type="submit" disabled={busy || newPassword.length < 8}
              className={cn("flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium",
                busy || newPassword.length < 8 ? "bg-zinc-800 text-zinc-500" : "bg-indigo-500 text-white hover:bg-indigo-400")}
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save it and sign in
            </button>
          </form>
        )}

        {done && (
          <p className="rounded-2xl border border-emerald-800/50 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
            That's set. Sign in with your new password.
          </p>
        )}

        {/* Ask for the link. */}
        {!resetToken && mode === "forgot" && (
          <form onSubmit={askForLink} className="space-y-3 rounded-2xl border border-zinc-800/70 bg-zinc-900/40 p-5">
            {sent ? (
              <>
                <p className="text-sm text-zinc-300">
                  If that address has a login here, a link is on its way to it. It works once
                  and lasts an hour.
                </p>
                <button type="button" onClick={() => { setMode("in"); setSent(false); }}
                  className="w-full rounded-lg bg-zinc-800 px-3 py-2 text-sm font-medium text-zinc-200 hover:bg-zinc-700">
                  Back to sign in
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-zinc-300">We'll email you a link to set a new one.</p>
                <div>
                  <label htmlFor="femail" className="mb-1 block text-xs text-zinc-400">Email</label>
                  <input
                    id="femail" type="email" autoComplete="username" required autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-indigo-500"
                  />
                </div>
                <button
                  type="submit" disabled={busy || !email}
                  className={cn("flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium",
                    busy || !email ? "bg-zinc-800 text-zinc-500" : "bg-indigo-500 text-white hover:bg-indigo-400")}
                >
                  {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Email me a link
                </button>
                <button type="button" onClick={() => setMode("in")}
                  className="w-full text-center text-[11px] text-zinc-500 hover:text-zinc-300">
                  Back to sign in
                </button>
              </>
            )}
          </form>
        )}

        {!resetToken && mode === "in" && (
        <form onSubmit={submit} className="space-y-3 rounded-2xl border border-zinc-800/70 bg-zinc-900/40 p-5">
          <div>
            <label htmlFor="email" className="mb-1 block text-xs text-zinc-400">Email</label>
            <input
              id="email" type="email" autoComplete="username" required autoFocus
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="password" className="mb-1 block text-xs text-zinc-400">Password</label>
            <input
              id="password" type="password" autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-indigo-500"
            />
          </div>

          {error && (
            <p role="alert" className="rounded-lg bg-rose-500/10 px-3 py-2 text-xs text-rose-300">{error}</p>
          )}

          <button
            type="submit" disabled={busy || !email || !password}
            className={cn(
              "flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium",
              busy || !email || !password
                ? "bg-zinc-800 text-zinc-500"
                : "bg-indigo-500 text-white hover:bg-indigo-400",
            )}
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Sign in
          </button>

          <button
            type="button"
            onClick={() => { setMode("forgot"); setError(null); }}
            className="w-full pt-1 text-center text-[11px] leading-relaxed text-zinc-500 hover:text-zinc-300"
          >
            Forgotten your password?
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
